import React, { useState, useEffect } from "react";
import { useAuth } from "../AuthContext";
import { API_URL } from "../services/api";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function LoginModal() {
  const { login, user, isLoginModalOpen, closeLoginModal } = useAuth();
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdminCode, setShowAdminCode] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    adminCode: "",
  });

  const ADMIN_CODE = process.env.REACT_APP_ADMIN_CODE;

  // Close modal when user is logged in
  useEffect(() => {
    if (user) {
      closeLoginModal();
    }
  }, [user, closeLoginModal]);

  // Reset form when modal opens
  useEffect(() => {
    if (isLoginModalOpen) {
      setError(null);
      setFormData({
        email: "",
        password: "",
        confirmPassword: "",
        name: "",
        adminCode: "",
      });
      setIsLogin(true);
    }
  }, [isLoginModalOpen]);

  if (!isLoginModalOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isLogin) {
        if (!formData.email || !formData.password) {
          throw new Error(t('login.errorFill'));
        }

        let response;
        try {
          response = await fetch(`${API_URL}/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: formData.email,
              password: formData.password,
            }),
          });
        } catch (networkError) {
          console.error("Login Network Error:", networkError);
          throw new Error(t('login.errorNetwork') || "Network error. Please check your connection.");
        }

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || "Login failed");
        }

        login(data.user, data.token);
        // Modal will close via useEffect
      } else {
        if (
          !formData.email ||
          !formData.password ||
          !formData.confirmPassword ||
          !formData.name
        ) {
          throw new Error(t('login.errorFill'));
        }
        if (formData.password !== formData.confirmPassword) {
          throw new Error(t('login.errorMatch'));
        }
        if (formData.password.length < 6) {
          throw new Error(t('login.errorLength'));
        }
 
        let isAdmin = false;
        if (formData.adminCode) {
          if (formData.adminCode !== ADMIN_CODE) {
            throw new Error(t('login.errorAdmin'));
          }
          isAdmin = true;
        }
 
        let response;
        try {
          response = await fetch(`${API_URL}/auth/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: formData.email,
              password: formData.password,
              name: formData.name,
              isAdmin,
              adminCode: formData.adminCode,
            }),
          });
        } catch (networkError) {
          console.error("Registration Network Error:", networkError);
          throw new Error(t('login.errorNetwork') || "Network error. Please check your connection.");
        }
 
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || "Registration failed");
        }
 
        login(data.user, data.token);
        // Modal will close via useEffect
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={closeLoginModal}>
      <div 
        className="w-full max-w-md bg-raya-card rounded-2xl shadow-xl overflow-hidden transform transition-all scale-100 opacity-100 p-8 relative border border-raya-border"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={closeLoginModal}
          className="absolute top-4 right-4 text-raya-text-tertiary hover:text-raya-accent transition-colors"
          title="Close"
        >
          <X size={24} />
        </button>

        <h1 className="text-3xl font-bold text-center mb-8 text-raya-text-primary">
          {isLogin ? t('login.loginTitle') : t('login.signupTitle')}
        </h1>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className="block text-sm font-semibold mb-2 text-raya-text-secondary">
                {t('login.fullName')}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t('login.placeholderName')}
                className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off text-raya-text-primary focus:outline-none focus:ring-2 focus:ring-raya-accent"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold mb-2 text-raya-text-secondary">{t('login.email')}</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('login.placeholderEmail')}
              className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off text-raya-text-primary focus:outline-none focus:ring-2 focus:ring-raya-accent"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2 text-raya-text-secondary">{t('login.password')}</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={t('login.placeholderPassword')}
              className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off text-raya-text-primary focus:outline-none focus:ring-2 focus:ring-raya-accent"
            />
          </div>

          {!isLogin && (
            <div>
              <label className="block text-sm font-semibold mb-2 text-raya-text-secondary">
                {t('login.confirmPassword')}
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder={t('login.placeholderConfirm')}
                className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off text-raya-text-primary focus:outline-none focus:ring-2 focus:ring-raya-accent"
              />
            </div>
          )}

          {!isLogin && (
            <div>
              <button
                type="button"
                onClick={() => setShowAdminCode(!showAdminCode)}
                className="text-sm text-raya-accent hover:underline w-full text-left mb-2"
              >
                {showAdminCode ? `▼ ${t('login.adminAccount')}` : `▶ ${t('login.adminAccount')}`}
              </button>
              {showAdminCode && (
                <div>
                  <label className="block text-sm font-semibold mb-2 text-raya-text-secondary">
                    {t('login.adminCode')}
                  </label>
                  <input
                    type="password"
                    name="adminCode"
                    value={formData.adminCode}
                    onChange={handleInputChange}
                    placeholder={t('login.placeholderAdminCode')}
                    className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off text-raya-text-primary focus:outline-none focus:ring-2 focus:ring-raya-accent"
                  />
                  <p className="text-xs text-raya-text-tertiary mt-1">
                    {t('login.adminHint')}
                  </p>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-raya-accent hover:bg-raya-accent-hover disabled:bg-raya-border text-raya-bg py-2 rounded-lg font-semibold transition"
          >
            {loading ? t('login.processing') : isLogin ? t('login.submitLogin') : t('login.submitSignup')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-raya-text-secondary mb-2">
            {isLogin ? t('login.noAccount') : t('login.hasAccount')}
          </p>
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setFormData({
                email: "",
                password: "",
                confirmPassword: "",
                name: "",
                adminCode: "",
              });
              setError(null);
            }}
            className="text-raya-accent hover:underline font-semibold"
          >
            {isLogin ? t('login.signupTitle') : t('login.loginTitle')}
          </button>
        </div>
      </div>
    </div>
  );
}
